<template>
  <div class="form-page">
    <div class="form-page__header">
      <h2 class="form-page__title">Information zum Antrag</h2>
      <p class="form-page__subtitle">* Pflichtfelder</p>
    </div>

    <!-- Info text block -->
    <div class="form-section">
      <div class="info-box info-box--blue">
        <span class="info-box__icon">ℹ️</span>
        <div>
          Im Folgenden können Sie einen Online-Antrag auf Einbürgerung stellen, wenn Sie mindestens das
          <strong>16. Lebensjahr</strong> vollendet haben. Die auf dem Serviceportal aufgeführte Gebühr wird
          als Vorschuss bei Einreichung des Online-Antrags erhoben. Ohne Bezahlung der Gebühren kann der
          Antrag nicht online eingereicht werden. Im Fall der Ablehnung des Einbürgerungsantrags oder einer
          späteren Antragsrücknahme werden die Gebühren nicht vollständig zurückerstattet.
        </div>
      </div>

      <div class="legal-block">
        <h3 class="legal-block__title">Hinweise zum Ausfüllen</h3>
        <p>
          Der Online-Antrag ist sehr umfangreich, so dass Sie etwa <strong>30 Minuten</strong> für das
          Ausfüllen des Antrags benötigen werden. Die auf dem Serviceportal Berlin genannten Nachweise sind
          verpflichtend einzureichen. Sollten Sie während des Ausfüllens feststellen, dass Ihnen ein Nachweis
          fehlt, können Sie den Antrag unterbrechen, abspeichern und zu einem späteren Zeitpunkt fortsetzen.
        </p>
      </div>

      <div class="legal-block">
        <h3 class="legal-block__title">Ausländische Urkunden</h3>
        <p>
          Für ausländische Urkunden wird eine <strong>Übersetzung in die deutsche Sprache</strong> benötigt.
          Der Übersetzer oder Dolmetscher muss bei einem deutschen Gericht zugelassen sein. Die Anerkennung
          ausländischer Urkunden kann von einer Legalisierung, Apostille oder einer Urkundenüberprüfung
          abhängig sein — dies hängt vom ausstellenden Staat ab. Umfangreiche Informationen hierzu finden
          Sie beim Auswärtigen Amt.
        </p>
      </div>
    </div>

    <!-- Kenntnisnahme -->
    <div class="form-section">
      <h3 class="section-title">
        Kenntnisnahme der Informationen zum Antrag auf Einbürgerung
        <span class="required-mark">*</span>
      </h3>
      <p style="font-size:13px; color:#555; margin-bottom:12px;">
        Die Informationen über die Voraussetzungen und die erforderlichen Nachweise können Sie jederzeit
        auf dem Service-Portal Berlin einsehen.
      </p>
      <label class="checkbox-label" :class="{ checked: modelValue.confirm_kenntnisnahme }">
        <input
          type="checkbox"
          class="checkbox-input"
          :checked="modelValue.confirm_kenntnisnahme"
          @change="update('confirm_kenntnisnahme', $event.target.checked)"
        />
        Ich habe die Informationen über die Voraussetzungen und die erforderlichen Nachweise zum Antrag
        auf Einbürgerung auf dem Service-Portal Berlin sorgfältig gelesen und zur Kenntnis genommen.
        <span class="required-mark">*</span>
      </label>
      <p v-if="showErrors && !modelValue.confirm_kenntnisnahme" class="field__error" style="margin-top:6px">
        ⚠ Bitte bestätigen Sie, dass Sie die Informationen zur Kenntnis genommen haben.
      </p>
    </div>

    <!-- Einholen von Auskünften -->
    <div class="form-section">
      <h3 class="section-title">
        Einholen von Auskünften
        <span class="required-mark">*</span>
      </h3>
      <div class="info-box info-box--yellow" style="margin-bottom:14px;">
        <span class="info-box__icon">⚖️</span>
        <div>
          Zur Bearbeitung des Einbürgerungsantrages wird die Zustimmung zur Auskunftseinholung durch das
          <strong>LEA bei anderen Behörden</strong> benötigt.
        </div>
      </div>

      <p style="font-size:14px; font-weight:600; color:#1a1a1a; margin-bottom:10px;">
        Ich bin damit einverstanden,
      </p>
      <ul class="consent-list">
        <li>dass die Staatsangehörigkeitsbehörde bei den zuständigen Meldebehörden Daten erhebt,</li>
        <li>
          dass die Träger von Leistungen nach den Sozialgesetzbüchern II oder XII, das Jugendamt und das
          Amt für Ausbildungsförderung, die Agentur für Arbeit zum Nachweis der gemachten Angaben befragt
          werden bzw. Auskunft erteilen (Einwilligung nach § 67b SGB X),
        </li>
        <li>
          dass die Finanzbehörden über meine Einkommensverhältnisse einschließlich etwaiger
          Steuerstrafverfahren Auskunft erteilen,
        </li>
        <li>dass die Ausländerbehörden Auskunft über meine ausländerrechtlichen Daten erteilen,</li>
        <li>
          dass die Auskunftserteilung gegenüber und Abfrage bei den Sicherheitsbehörden und Gerichten
          (Polizei, Bundeszentralregister, Verfassungsschutz, Staatsanwaltschaften und Gerichte) erfolgt.
        </li>
      </ul>

      <label class="checkbox-label" :class="{ checked: modelValue.confirm_auskuenfte }" style="margin-top:14px;">
        <input
          type="checkbox"
          class="checkbox-input"
          :checked="modelValue.confirm_auskuenfte"
          @change="update('confirm_auskuenfte', $event.target.checked)"
        />
        Ich bin damit einverstanden. <span class="required-mark">*</span>
      </label>
      <p v-if="showErrors && !modelValue.confirm_auskuenfte" class="field__error" style="margin-top:6px">
        ⚠ Bitte erteilen Sie Ihre Einwilligung zur Auskunftseinholung, um fortzufahren.
      </p>
    </div>

    <p class="required-note"><span class="required-mark">*</span> Pflichtfelder</p>
  </div>
</template>

<script>
export default {
  name: 'NatStepInfo',
  props: {
    modelValue: { type: Object, required: true },
    showErrors: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const update = (key, val) => emit('update:modelValue', { ...props.modelValue, [key]: val });
    return { update };
  },
};
</script>

<style scoped src="../form.css"></style>

<style scoped>
.consent-list {
  margin: 0 0 4px 0;
  padding-left: 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.consent-list li {
  font-size: 14px;
  color: #374151;
  line-height: 1.55;
}
</style>
